// src/utils/formatters.js

// Normaliza texto (remove acentos e põe minúsculo) para busca
export const normalizeText = (text) => {
  return String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

// Formata valor monetário (R$ 1.200,00)
export const formatMoney = (val) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(val || 0);
};

// Formata data (Se for Timestamp do Firebase ou String)
export const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  // Se for objeto timestamp do firestore
  if (dateStr?.toDate) return dateStr.toDate().toLocaleString("pt-BR");
  // Se for string
  return dateStr.split(" ")[0];
};

// Helper para normalizar data ao meio-dia evitando anomalias de fuso horário
export const normalizeToNoon = (dateInput) => {
  if (!dateInput) return null;
  // Se for string pura "YYYY-MM-DD" (ex: vinda do PocketBase ou BrasilAPI),
  // converte diretamente usando os componentes locais para não sofrer deslocamento UTC (ex: virar 21h do dia anterior)
  if (typeof dateInput === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateInput.trim())) {
    const [y, m, d] = dateInput.trim().split("-").map(Number);
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }
  const rawDate = dateInput.toDate ? dateInput.toDate() : new Date(dateInput);
  if (isNaN(rawDate.getTime())) return null;

  return new Date(
    rawDate.getFullYear(),
    rawDate.getMonth(),
    rawDate.getDate(),
    12, 0, 0, 0
  );
};

// Formata data para o padrão civil ISO "YYYY-MM-DD"
export const formatDateToIso = (dateInput) => {
  const d = normalizeToNoon(dateInput);
  if (!d) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Cache global em memória para feriados e dias não úteis cadastrados
let globalFeriadosSet = new Set();

/**
 * Atualiza o conjunto de feriados em memória para consultas em tempo constante O(1)
 * @param {Set|Array} feriados - Set ou Array de strings "YYYY-MM-DD" ou objetos com campo .data
 */
export const setFeriadosCache = (feriados) => {
  if (feriados instanceof Set) {
    globalFeriadosSet = feriados;
  } else if (Array.isArray(feriados)) {
    globalFeriadosSet = new Set(
      feriados
        .filter((f) => f && f.ativo !== false)
        .map((f) => (typeof f === "string" ? f : f.data))
        .filter(Boolean)
    );
  }
};

/**
 * Retorna o Set atual de feriados cadastrados em memória
 */
export const getFeriadosCache = () => globalFeriadosSet;

/**
 * Determina se uma data é dia útil (não é sábado, domingo ou feriado cadastrado)
 * @param {Date|string} date - Data a ser verificada
 * @param {Set} [customFeriadosSet] - Conjunto opcional de feriados para override
 * @returns {boolean} true se for dia útil fabril
 */
export const isDiaUtil = (date, customFeriadosSet = null) => {
  if (!date) return false;
  const d = normalizeToNoon(date);
  if (!d) return false;

  const dayOfWeek = d.getDay();
  // 0 = Domingo, 6 = Sábado
  if (dayOfWeek === 0 || dayOfWeek === 6) return false;

  const iso = formatDateToIso(d);
  const feriados = customFeriadosSet || globalFeriadosSet;
  if (feriados && feriados.has(iso)) return false;

  return true;
};

// Calcula a diferença em dias úteis entre duas datas (exclui fins de semana e feriados cadastrados)
export const getBusinessDaysBetween = (inicio, fim, customFeriadosSet = null) => {
  if (!inicio || !fim) return 0;
  const start = normalizeToNoon(inicio);
  const end = normalizeToNoon(fim);
  if (!start || !end || start >= end) return 0;

  let count = 0;
  let curDate = new Date(start.getTime());

  while (curDate < end) {
    curDate.setHours(12, 0, 0, 0);
    if (curDate >= end) break;

    if (isDiaUtil(curDate, customFeriadosSet)) {
      count++;
    }

    curDate.setDate(curDate.getDate() + 1);
  }

  return count;
};

// Retorna os dias úteis decorridos desde a data inicial até hoje (ou data de referência)
export const getBusinessDaysDiff = (startDate, endDate = new Date(), customFeriadosSet = null) => {
  if (!startDate) return 0;
  return getBusinessDaysBetween(startDate, endDate, customFeriadosSet);
};

// Adiciona n dias úteis a uma data, pulando fins de semana e feriados cadastrados
export const addBusinessDays = (dateInput, n, customFeriadosSet = null) => {
  if (!dateInput) return null;
  const baseDate = normalizeToNoon(dateInput);
  if (!baseDate) return null;

  let remaining = Math.round(Number(n) || 0);
  if (remaining <= 0) return baseDate;

  const result = new Date(baseDate.getTime());
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    if (isDiaUtil(result, customFeriadosSet)) {
      remaining--;
    }
  }

  return result;
};
